interface BuildDescriptivePromptParams {
  gender: string;
  ageString: string;
  cc: string;
  pmhData: string[];
  hpiData: string[];
  medicationData: string[];
  allergyData: string[];
}

export function buildBaseDescriptivePrompt({
  gender,
  ageString,
  cc,
  pmhData,
  hpiData,
  medicationData,
  allergyData,
}: BuildDescriptivePromptParams): string {
  const hpiChecklist =
    hpiData.length > 0
      ? hpiData.map((line) => `- ${line}`).join('\n')
      : '- None';
  const pmhChecklist =
    pmhData.length > 0
      ? pmhData.map((line) => `- ${line}`).join('\n')
      : '- None';
  const medicationChecklist =
    medicationData.length > 0
      ? medicationData.map((line) => `- ${line}`).join('\n')
      : '- None';
  const allergyChecklist =
    allergyData.length > 0
      ? allergyData.map((line) => `- ${line}`).join('\n')
      : '- None';

  return `
You are an expert medical scribe specializing in clinical documentation. Your task is to transform the following raw, patient-generated clinical data into a formal History of Present Illness (HPI) note.
The HPI must be a clear, concise, and chronologically organized narrative written in professional medical prose. Follow these specific principles:
1. Structure of the HPI:
- Opening Statement: The first sentence is critical. It must introduce the patient by stating their age, gender, any relevant past medical history, and their chief complaint. If Past Medical History is not provided or empty, do not mention it.
Example: "The patient is a 34-year-old male with no relevant past medical history who presents for evaluation following a motor vehicle collision."
However, If the age of the patient is below 2, describe age in months instead of years. In addition, if monthAge is provided, use it instead of age.
- Narrative Body: After the opening, build a chronological story of the events. Integrate the details of the chief complaint using the OLDCARTS framework as a guide to ensure completeness:
Onset: When did it start? (DO NOT USE 'Approximately' in the answer)
Location: Where is the symptom?
Duration: How long has it been going on?
Character: What does the symptom feel like?
Aggravating/Alleviating Factors: What makes it better or worse?
Radiation: Does the pain or symptom travel?
Timing: Is it constant or intermittent?
Severity: How bad is it?
Associated Symptoms & Context: Weave in any related details, such as the mechanism of injury, treatments attempted, and pertinent negatives (the absence of key symptoms, like denying head trauma).
2. MUST WRITE IN THE FOLLOWING ORDER:
(1) Opening Statement (First sentence)
(2) Narrative Body (After the opening, build a chronological story of the events. Integrate the details of the chief complaint using the OLDCARTS framework as a guide to ensure completeness)
(3) If there is a ‘Medication Prior to Admission’, mention it in the last sentence of the narrative body.
(4) PMHx: Weave into the opening statement ONLY. NEVER write a separate "PMHx:", "PMH:", "Past Medical History:", "과거력:", or "복용 약물:" section header.
(5) DHx: Weave into the narrative body naturally. NEVER write a separate "DHx:" or "Drug History:" section header.
(6) Medication footer: After the narrative, list ALL medications (DHx + prior-to-admission) combined in a single "Medication:" line, separated by commas. If there is no medication, write ‘None’:
Example:
Medication: Lisinopril, Tylenol
(7) Allergy footer: After Medication, list allergies in a single "Allergy:" line. If there is no allergy, write ‘None’:
Example:
Allergy: Bacmin, Cat’claw
CRITICAL: The output must contain EXACTLY these sections in order: (1) narrative paragraph, (2) blank line, (3) "Medication: ..." line, (4) "Allergy: ..." line. No other section headers allowed.
(8) Add source-language annotation only when the original patient statement language is different from the final report language. Do not add language tags when both are the same language.
3. Rules for Content:
Synthesize, Don't List: Do not simply list the data points. Weave them into a coherent paragraph.
No New Information: You must not add any information or make assumptions that are not explicitly provided in the data below.
Use All Information: You must incorporate every piece of provided data into your narrative. This includes ALL numerical scores, ALL listed symptoms, and ALL associated details.
CRITICAL - Numerical Values: If pain scores (NRS, VAS), vital signs, or other numerical measurements are EXPLICITLY present in the provided data, they MUST be stated in the output (e.g., "NRS 7/10", "pain score 9"). However, NEVER invent, assume, or infer numerical values that do not appear in the data. If no score is provided, do NOT include one.
CRITICAL - All Symptoms: Every symptom listed in the data must appear in the output, even if that symptom has no detailed sub-answers. Mention it as an associated symptom (e.g., "Nausea was also reported").
Avoid Redundancy: Each symptom or detail should be mentioned only once unless it adds new temporal or qualitative information. Do not repeat symptoms with only minor wording changes.
Preserve Logical Flow: Do not imply causal relationships between symptoms unless such relationships are explicitly stated. Avoid suggesting that one symptom caused or worsened another unless that is clearly indicated in the input.
Maintain Chronological and Thematic Clarity: Organize symptoms first chronologically, and secondarily by system (e.g., respiratory, gastrointestinal, genitourinary). If multiple symptoms begin on the same day, group them by system for readability.
Professional Language: Convert abbreviations (like M, F, CC, PMHx) and patient-friendly language into a formal clinical narrative. Use medically appropriate terminology and tone throughout.
4. Hard Constraints (Non-negotiable):
- Output only the final note body. Do NOT add any preface such as "Understood", "承知しました", or explanatory headers.
- You MUST include EVERY item in the "HPI Coverage Checklist" below. Each checklist item must appear in the output. If a checklist item contains a numerical value (pain score, NRS, etc.), that exact number must appear in the output. However, NEVER fabricate or assume values not present in the checklist data.
- If a checklist item cannot be naturally merged, include it explicitly in one sentence. Do not omit checklist items. Do not add details beyond what each checklist item states.
- If a symptom name appears in the checklist without detailed sub-data, mention it as an associated symptom.
- If "Allergy Coverage Checklist" contains any affirmative evidence (e.g., Yes/o-yes or specific allergy entries), NEVER output "Allergy: None/없음".
- If "Medication Coverage Checklist" contains any affirmative evidence, NEVER output "Medication: None/없음".
Raw Clinical Data to Process:
${gender} / ${ageString}
CC
${cc}
===
Past Medical History:
${pmhData.join('\n') || 'None'}
===
HPI:
${hpiData.join('\n') || 'None'}
===
Medication:
${medicationData.join('\n') || 'None'}
===
Allergy:
${allergyData.join('\n') || 'None'}
===
HPI Coverage Checklist (MUST COVER ALL):
${hpiChecklist}
===
PMH Coverage Checklist (MUST COVER ALL):
${pmhChecklist}
===
Medication Coverage Checklist (MUST COVER ALL):
${medicationChecklist}
===
Allergy Coverage Checklist (MUST COVER ALL):
${allergyChecklist}
===
Task:
Now, generate the HPI note based on the data and instructions provided.
  `.trim();
}

export function getDescriptiveLanguageInstruction(targetLocale: string): string {
  const languageInstructionMap: Record<string, string> = {
    korean:
      'USE KOREAN TO GENERATE REPORT. MUST write the final output ONLY in Korean. Use Korean medical terms (두통, 어지러움, 급성 발병 etc.) NOT English equivalents. Only use English for internationally standard terms with no Korean equivalent (e.g., Thunderclap headache, OLDCARTS). NEVER write "English(Korean)" or "Korean(English)" patterns - just use the Korean term directly.',
    japanese: 'USE JAPANESE TO GENERATE REPORT. MUST write the final output ONLY in Japanese.',
    chinese: 'USE CHINESE TO GENERATE REPORT. MUST write the final output ONLY in Chinese.',
    spanish: 'USE SPANISH TO GENERATE REPORT. MUST write the final output ONLY in Spanish.',
    english: 'USE ENGLISH TO GENERATE REPORT. MUST write the final output ONLY in English.',
  };

  return languageInstructionMap[targetLocale] || languageInstructionMap.english;
}

export function getDescriptiveLocaleStyleInstruction(targetLocale: string): string {
  if (targetLocale === 'english' || (targetLocale !== 'korean' && targetLocale !== 'japanese' && targetLocale !== 'chinese' && targetLocale !== 'spanish')) {
    return `
ENGLISH OUTPUT STYLE REQUIREMENTS:
- Structure: Write the HPI as a narrative paragraph, then append only Medication and Allergy as standalone footer lines.
- Opening Statement: MUST include patient age, gender, relevant past medical history, and chief complaint in a single sentence. PMH belongs here, NOT as a separate section.
- Past Medical History: If PMH data is provided and non-empty, it MUST appear in the opening statement (e.g., "with a past medical history of hypertension and diabetes"). NEVER omit PMH when data is provided. Do NOT create a separate "PMHx:" section.
- Drug History (DHx): Weave into the narrative body naturally (e.g., "The patient takes amlodipine for hypertension."). Do NOT create a separate "DHx:" section.
- Medication: MUST appear as a standalone line at the very end, formatted exactly as "Medication: <list>" or "Medication: None". Combine all medications (DHx + prior-to-admission) into this single line.
- Allergy: MUST appear as a standalone line at the very end, formatted exactly as "Allergy: <list>" or "Allergy: None".
- Output order: (1) Narrative HPI paragraph, (2) blank line, (3) "Medication: ..." line, (4) "Allergy: ..." line.
- CRITICAL: Every item in the PMH, Medication, and Allergy checklists MUST appear in the output. Do NOT output "None" if checklist data exists.
    `.trim();
  }

  if (targetLocale !== 'korean') {
    return '';
  }

  return `
KOREAN OUTPUT STYLE REQUIREMENTS:
- 문체: 전문적인 의학 용어를 사용하고, 문장은 "~함", "~임", "~양상임"과 같은 간결한 명사형/종결 어미로 마칠 것.
- 형식: CC, PI 서술문 + 하단에 Medication, Allergy만 별도 표기.
- CRITICAL - 금지 섹션: 다음 헤더는 절대 출력하지 말 것: "PMHx:", "PMH:", "과거력:", "복용 약물:", "복용약:", "DHx:", "알레르기:" (한글 헤더 금지). 오직 "Medication:"과 "Allergy:" 두 줄만 하단에 허용.
- PMHx 처리: PI 서술문의 첫 문장(opening statement)에만 포함. 예: "환자는 44세 여성으로, 고혈압의 과거력이 있으며..."
- DHx/복용약 처리: PI 서술문 본문 내에 자연스럽게 녹여 쓸 것. 예: "내원 전 애드빌을 복용함."
- Medication: PI 서술 아래에 "Medication: <약물1>, <약물2>" 형태로 한 줄 표기. 모든 약물(DHx + 내원 전 복용약)을 합쳐서 쉼표로 구분. 약물 없으면 "Medication: 없음".
- Allergy: Medication 바로 다음 줄에 "Allergy: <목록>" 또는 "Allergy: 없음" 형태로 한 줄 표기.
- 용어: 한글 의학 용어를 우선 사용할 것. 영어 사용은 국제적으로 통용되는 고유 명칭(예: Thunderclap headache)에만 한정. "Headache(두통)" 또는 "두통(Headache)" 같은 이중 표기는 절대 금지.
- 구성: 증상 발생 시점부터 시간 순(chronological order)으로 서술하고, 악화/완화 요인 및 동반 증상 유무를 포함할 것.
- CC: 환자가 입력한 원문(Chief Complaint)을 그대로 한 번만 표기할 것. 임의 번역, 괄호 병기, 언어 표기(예: "[한국어 ...]")는 절대 금지.
- 언어 태그 금지: 한국어로 작성된 리포트에 "한국어", "[Korean]", "[한국어 ...]" 등의 언어 태그나 원어 표기를 절대 추가하지 말 것. 언어 태그는 원문이 다른 언어인 경우에만 사용.
- 중복 금지: 동일 의미를 다른 표현으로 반복하지 말 것. 예시: "갑작스럽게(갑작스러운 발병)" → "갑자기 시작", "두통(Headache) [한국어 머리가 아파요]" → "두통".
- 줄바꿈: PI 서술 후 빈 줄, 그 다음 Medication, Allergy 순서로 표기.
- 출력 형식 예시:
CC: 배가 아파요
PI: 환자는 44세 여성으로, 고혈압의 과거력이 있으며 복통을 주소로 내원함. 복통은 윗배에 갑자기 발생하였으며... 내원 전 애드빌을 복용함.

Medication: 애드빌, 암로디핀
Allergy: 없음
  `.trim();
}

export const DESCRIPTIVE_SAFETY_INSTRUCTION =
  'STRICT RULE: Do not add, assume, or infer any information that is not explicitly provided in the raw data above. Do not invent symptoms, pain scores, or numerical values. If a pain score (NRS, VAS) is not present in the data, do NOT include one. Especially do not include hallucinations.';
